import React, { useState, useEffect } from 'react';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, FormGroup, FieldError } from '../../styles/common/input.styled';
import { Button } from '../../styles/common/Button.styled';
import Modal from '../common/Modal';
import PageState from '../common/PageState';
import { getRoles, getPhones, getMainPhoneList, registerUser, RoleInfoResponse, PhoneInfoResponse, MainPhoneResponse } from '../../api/user';
import { listTeams, TeamResponse } from '../../api/team';
import { formatPhoneNumber } from '../../utils/format';
import toast from 'react-hot-toast';
import { getErrorMessage, getResponseMessage } from '../../utils/apiMessage';

interface AccountCreateModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


const AccountCreateModal: React.FC<AccountCreateModalProps> = ({ onClose, onSuccess }) => {
  const [roles, setRoles] = useState<RoleInfoResponse[]>([]);
  const [phones, setPhones] = useState<PhoneInfoResponse[]>([]);
  const [mainPhones, setMainPhones] = useState<MainPhoneResponse[]>([]);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    loginId: '',
    password: '',
    username: '',
    roleId: '',
    teamId: '',
    phone: '',
    dodTargetId: ''
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let isCurrent = true;
    setIsInitialLoading(true);
    setHasLoadError(false);
    const fetchData = async () => {
      try {
        const [rolesData, phonesData, mainPhonesData, teamsData] = await Promise.all([
          getRoles(),
          getPhones(),
          getMainPhoneList(),
          listTeams({ limit: 100, pg: 1 })
        ]);
        if (!isCurrent) return;
        setRoles(rolesData);
        setPhones(phonesData);
        setMainPhones(mainPhonesData.content);
        setTeams(teamsData.content);
      } catch (error) {
        if (!isCurrent) return;
        console.error('Failed to fetch modal data:', error);
        setHasLoadError(true);
      } finally {
        if (isCurrent) setIsInitialLoading(false);
      }
    };
    fetchData();
    return () => { isCurrent = false; };
  }, [retryKey]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateRequiredFields = () => {
    const nextErrors: Record<string, string> = {};

    const loginId = formData.loginId.trim();

    if (!loginId) {
      nextErrors.loginId = '로그인 아이디를 입력해주세요.';
    } else if (!EMAIL_PATTERN.test(loginId)) {
      nextErrors.loginId = '이메일 형식으로 입력해주세요.';
    }

    if (!formData.password.trim()) nextErrors.password = '비밀번호를 입력해주세요.';
    if (!formData.username.trim()) nextErrors.username = '사용자명을 입력해주세요.';
    if (!formData.teamId) nextErrors.teamId = '소속 팀을 선택해주세요.';

    setFieldErrors(nextErrors);

    const firstErrorField = Object.keys(nextErrors)[0];
    if (firstErrorField) {
      window.setTimeout(() => {
        const element = document.querySelector<HTMLElement>(`[name="${firstErrorField}"]`);
        element?.focus();
      }, 0);
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isInitialLoading || hasLoadError) return;
    if (!validateRequiredFields()) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await registerUser({
        loginId: formData.loginId.trim(),
        password: formData.password,
        username: formData.username,
        teamId: Number(formData.teamId),
        ...(formData.phone ? { phone: formData.phone } : {}),
        ...(formData.roleId ? { roleId: Number(formData.roleId) } : {}),
        ...(formData.dodTargetId ? { dodTargetId: Number(formData.dodTargetId) } : {}),
        kind: 'CALLIS'
      });
      toast.success(getResponseMessage(response, '계정이 등록되었습니다.'));
      onSuccess();
    } catch (error) {
      console.error('Failed to register user:', error);
      toast.error(getErrorMessage(error, '사용자 등록에 실패했습니다.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title="신규 계정 등록" onClose={onClose} historyMode="none" width={520} footer={<>
      <Button $variant="secondary" onClick={onClose}>취소</Button>
      <Button onClick={handleSubmit} disabled={isLoading || isInitialLoading || hasLoadError}>{isLoading ? '등록 중...' : '등록하기'}</Button>
    </>}>
      {isInitialLoading && <PageState status="loading" title="선택 항목을 불러오는 중입니다." />}
      {hasLoadError && <PageState status="error" title="선택 항목을 불러오지 못했습니다." onRetry={() => setRetryKey(key => key + 1)} />}
        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-loginId">로그인 아이디</ConsoleLabel>
          <ConsoleInput id="AccountCreateModal-loginId"
            type="email"
            name="loginId"
            value={formData.loginId}
            onChange={handleChange}
            placeholder="이메일을 입력하세요"
            autoComplete="username"
            aria-invalid={Boolean(fieldErrors.loginId)}
            aria-describedby={fieldErrors.loginId ? 'account-create-loginId-error' : undefined}
          />
          {fieldErrors.loginId && <FieldError id="account-create-loginId-error">{fieldErrors.loginId}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-password">비밀번호</ConsoleLabel>
          <ConsoleInput id="AccountCreateModal-password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="비밀번호를 입력하세요"
            aria-invalid={Boolean(fieldErrors.password)}
            aria-describedby={fieldErrors.password ? 'account-create-password-error' : undefined}
          />
          {fieldErrors.password && <FieldError id="account-create-password-error">{fieldErrors.password}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-username">사용자명</ConsoleLabel>
          <ConsoleInput id="AccountCreateModal-username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="이름을 입력하세요"
            aria-invalid={Boolean(fieldErrors.username)}
            aria-describedby={fieldErrors.username ? 'account-create-username-error' : undefined}
          />
          {fieldErrors.username && <FieldError id="account-create-username-error">{fieldErrors.username}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-teamId">소속 팀</ConsoleLabel>
          <ConsoleSelect id="AccountCreateModal-teamId"
            name="teamId"
            value={formData.teamId}
            onChange={handleChange}
            aria-label="소속 팀"
            aria-invalid={Boolean(fieldErrors.teamId)}
            aria-describedby={fieldErrors.teamId ? 'account-create-teamId-error' : undefined}
          >
            <option value="">소속 팀을 선택하세요</option>
            {teams.map(team => (
              <option key={team.id} value={team.id}>{team.displayName}</option>
            ))}
          </ConsoleSelect>
          {fieldErrors.teamId && <FieldError id="account-create-teamId-error">{fieldErrors.teamId}</FieldError>}
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-roleId">역할 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountCreateModal-roleId"
            name="roleId"
            value={formData.roleId}
            onChange={handleChange}
          >
            <option value="">역할을 선택하세요</option>
            {roles.map(role => (
              <option key={role.id} value={role.id}>{role.name}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-phone">내선번호 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountCreateModal-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          >
            <option value="">내선번호를 선택하세요</option>
            {phones.map(p => (
              <option key={p.id} value={p.phone}>{p.phone}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

        <FormGroup>
          <ConsoleLabel htmlFor="AccountCreateModal-dodTargetId">발신번호 (선택)</ConsoleLabel>
          <ConsoleSelect id="AccountCreateModal-dodTargetId"
            name="dodTargetId"
            value={formData.dodTargetId}
            onChange={handleChange}
          >
            <option value="">발신번호를 선택하세요</option>
            {mainPhones.map(p => (
              <option key={p.id} value={p.id}>{formatPhoneNumber(p.phone)}</option>
            ))}
          </ConsoleSelect>
        </FormGroup>

    </Modal>
  );
};

export default AccountCreateModal;
